/**
 * A subtitle's word-for-word line (Requirement 61): each French word with
 * what it says literally, in the French order — "Je vous présente le…" over
 * "I to you present the…". It shows students how the sentence is built,
 * which the English subtitle (its meaning) doesn't.
 *
 * The glosses are yasna's: each annotated word's sense says what it means
 * literally, per grammatical role (yasna's "word for word" card, read with
 * its wordForWord(); hoja's copy of the rule is literalOf in
 * src/learner/meaning.js). Hoja only knows the sentence: which role the
 * word plays here (`role` on the annotation) and where its words are.
 */

/** The words of a subtitle line, as the student page splits it. */
export const wordsOf = (text) => (text || '').split(/\s+/).filter(Boolean);

// A word for matching: lower case, without the punctuation around it.
// "Présente," and "présente" are the same word; "j'en" stays "j'en".
export const normalWord = (w) => (w || '')
  .toLowerCase()
  .replace(/’/g, "'")
  .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');

// Where an annotation's words are in the line: the places tapped in the
// subtitle (wordIndices), else the first run of words matching its text
// that no other annotation has taken.
function placesOf(annotation, normal, taken) {
  if (Array.isArray(annotation.wordIndices) && annotation.wordIndices.length > 0) {
    return [...annotation.wordIndices].filter((i) => i >= 0 && i < normal.length).sort((a, b) => a - b);
  }
  const span = wordsOf(annotation.surfaceText).map(normalWord).filter(Boolean);
  if (span.length === 0) return [];
  for (let i = 0; i + span.length <= normal.length; i += 1) {
    const places = span.map((_, k) => i + k);
    if (places.some((p) => taken.has(p))) continue;
    if (span.every((w, k) => normal[i + k] === w)) return places;
  }
  return [];
}

/**
 * The line for these words: [{ word, gloss, annotationId }], one per word.
 * gloss is '' where no annotated word's sense says it; annotationId is the
 * annotation covering the word, or null when no annotation does.
 *
 * annotations — the subtitle's annotated words
 * literals — { annotationId: [{ word, gloss }] }, what each annotation's
 *   sense says literally in its role (one part for a word; one per part of
 *   an expression, "il y a" → it / there / has)
 */
export function wordForWordLine(words, annotations = [], literals = {}) {
  const normal = words.map(normalWord);
  const glosses = words.map(() => '');
  const owners = words.map(() => null);
  const taken = new Set();
  (annotations || []).forEach((a) => {
    if (!a) return;
    const places = placesOf(a, normal, taken);
    if (places.length === 0) return;
    places.forEach((p) => {
      taken.add(p);
      if (owners[p] === null) owners[p] = a.id;
    });
    const parts = literals[a.id] || [];
    if (parts.length === 0) return;
    if (parts.length === places.length) {
      // One gloss per word of the expression, in order.
      places.forEach((p, k) => { if (!glosses[p]) glosses[p] = parts[k].gloss; });
    } else {
      // Parts that don't map one to one ("bonjour" = bon + jour): all
      // under the expression's first word.
      const first = places[0];
      if (!glosses[first]) glosses[first] = parts.map((x) => x.gloss).join(' ');
    }
  });
  return words.map((word, i) => ({ word, gloss: glosses[i], annotationId: owners[i] }));
}

/** Whether a line has anything to show. */
export const hasGlosses = (line) => line.some((p) => p.gloss);
